import { SelectProps, MultiselectProps } from "@cloudscape-design/components";
import type { StageDisplayConfig } from "./components/cardTypes";

export const PAGE_SIZE_OPTIONS: SelectProps.Options = [
  { value: "10", label: "10" },
  { value: "20", label: "20" },
  { value: "50", label: "50" },
  { value: "100", label: "100" },
];

export type MetricCardType = "otdc" | "output_hit_rate" | "machine_downtime" | "line_utilization" | "fpy" | "phic_alert_status";

export interface MetricCardDef {
  id: MetricCardType;
  title: string;
  subtitle: string;
  color: string;
}

export const TIME_GRANULARITY_OPTIONS: SelectProps.Options = [
  { label: "By Date", value: "day" },
  { label: "By Week", value: "week" },
  { label: "By Month", value: "month" },
];

export const EOM_OPTIONS: MultiselectProps.Options = [
  { label: "All", value: "__all__" },
  { label: "Yes", value: "yes" },
  { label: "No", value: "no" },
];

export const WEEK_OPTIONS: SelectProps.Options = Array.from({ length: 52 }, (_, i) => ({
  label: `W${String(i + 1).padStart(2, "0")}`,
  value: `W${String(i + 1).padStart(2, "0")}`,
}));

export const MONTH_OPTIONS: SelectProps.Options = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
].map((m, i) => ({ label: m, value: String(i + 1).padStart(2, "0") }));

const currentYear = new Date().getFullYear();
export const YEAR_OPTIONS: SelectProps.Options = Array.from({ length: 5 }, (_, i) => {
  const y = String(currentYear - 2 + i);
  return { label: y, value: y };
});

export const METRIC_CARDS_ROW1: MetricCardDef[] = [
  { id: "otdc", title: "OTD-C Hit Rate", subtitle: "", color: "#0972d3" },
  { id: "output_hit_rate", title: "Output Hit Rate", subtitle: "", color: "#6941c6" },
  { id: "machine_downtime", title: "Machine Downtime", subtitle: "", color: "#d13212" },
  { id: "line_utilization", title: "Line Utilization", subtitle: "", color: "#b45309" },
];

export const METRIC_CARDS_ROW2: MetricCardDef[] = [
  { id: "fpy", title: "FPY (First Pass Yield)", subtitle: "", color: "#1d8102" },
  { id: "phic_alert_status", title: "PHIC Alert Status", subtitle: "", color: "#c05621" },
];

export const OUTPUT_HR_STAGES: StageDisplayConfig[] = [
  { key: "SMT", label: "SMT", target: 95, valueField: "hit_rate", prevField: "prev_hit_rate", details: [
    { label: "Actual", field: "actual_output", format: "number" },
    { label: "Plan", field: "plan_output", format: "number" },
  ]},
  { key: "Testing", label: "TESTING", target: 95, valueField: "hit_rate", prevField: "prev_hit_rate", details: [
    { label: "Actual", field: "actual_output", format: "number" },
    { label: "Plan", field: "plan_output", format: "number" },
  ]},
  { key: "Pack Out", label: "PACK OUT", target: 95, valueField: "hit_rate", prevField: "prev_hit_rate", details: [
    { label: "Actual", field: "actual_output", format: "number" },
    { label: "Plan", field: "plan_output", format: "number" },
  ]},
];

export const FPY_STAGES: StageDisplayConfig[] = [
  { key: "SMT", label: "SMT", target: 98, valueField: "fpy", prevField: "prev_fpy", details: [
    { label: "RPY Actual", field: "rpy", format: "percent" },
    { label: "FPY Cum", field: "cum_fpy", format: "percent" },
  ]},
  { key: "Testing", label: "TESTING", target: 97, valueField: "fpy", prevField: "prev_fpy", details: [
    { label: "RPY Actual", field: "rpy", format: "percent" },
    { label: "FPY Cum", field: "cum_fpy", format: "percent" },
  ]},
  { key: "Assembly", label: "ASSEMBLY", target: 97, valueField: "fpy", prevField: "prev_fpy", details: [
    { label: "RPY Actual", field: "rpy", format: "percent" },
    { label: "FPY Cum", field: "cum_fpy", format: "percent" },
  ]},
];

export const MACHINE_DT_STAGES: StageDisplayConfig[] = [
  { key: "SMT", label: "SMT", target: 2, valueField: "machine_downtime", prevField: "prev_machine_downtime" },
  { key: "Testing", label: "TESTING", target: 3, valueField: "machine_downtime", prevField: "prev_machine_downtime" },
];

export interface ListColumnDef {
  stageKey?: string;
  label: string;
  width: string;
  valueField: string;
  target: number;
  warningOffset?: number;
  invert?: boolean;
}

export interface ListCardConfig {
  dataKey: string;
  columns: ListColumnDef[];
  statusColorOverride?: Record<string, string>;
}

export const LIST_VIEW_CONFIGS: Record<Exclude<MetricCardType, "phic_alert_status">, ListCardConfig> = {
  output_hit_rate: {
    dataKey: "output_hit_rate",
    columns: [
      { stageKey: "SMT", label: "SMT", width: "54px", valueField: "hit_rate", target: 95, warningOffset: 5 },
      { stageKey: "Testing", label: "Testing", width: "56px", valueField: "hit_rate", target: 95, warningOffset: 5 },
      { stageKey: "Pack Out", label: "Pack Out", width: "64px", valueField: "hit_rate", target: 95, warningOffset: 5 },
    ],
  },
  fpy: {
    dataKey: "fpy",
    columns: [
      { stageKey: "SMT", label: "SMT", width: "50px", valueField: "fpy", target: 98, warningOffset: 2 },
      { stageKey: "Testing", label: "Testing", width: "56px", valueField: "fpy", target: 97, warningOffset: 2 },
      { stageKey: "Assembly", label: "Assembly", width: "62px", valueField: "fpy", target: 97, warningOffset: 2 },
    ],
  },
  machine_downtime: {
    dataKey: "machine_downtime",
    columns: [
      { stageKey: "SMT", label: "SMT", width: "50px", valueField: "machine_downtime", target: 2, warningOffset: 1, invert: true },
      { stageKey: "Testing", label: "FATP", width: "50px", valueField: "machine_downtime", target: 3, warningOffset: 1, invert: true },
    ],
  },
  otdc: {
    dataKey: "otdc",
    columns: [
      { label: "OTD-C", width: "50px", valueField: "hit_rate", target: 95 },
    ],
  },
  line_utilization: {
    dataKey: "line_utilization",
    columns: [
      { label: "Utilization", width: "60px", valueField: "line_utilization", target: 90 },
    ],
    statusColorOverride: { at_risk: "#d7a80b" },
  },
};

export const STATUS_CONFIG: Record<string, { label: string; color: string; bg: string; border: string }> = {
  on_target: { label: "On Target", color: "#1d8102", bg: "rgba(34,197,94,0.1)", border: "rgba(34,197,94,0.3)" },
  below_target: { label: "Below Target", color: "#8D6E0A", bg: "rgba(250,173,20,0.1)", border: "rgba(250,173,20,0.3)" },
  at_risk: { label: "At Risk", color: "#d13212", bg: "rgba(239,68,68,0.1)", border: "rgba(239,68,68,0.3)" },
};

export interface SummaryDetailDef {
  label: string;
  field?: string;
  showGapColor?: boolean;
  percent?: boolean;
}

export interface SummaryCardConfig {
  target: number;
  valueField: string;
  prevField: string;
  cumLabel: string;
  cumField: string;
  details: SummaryDetailDef[];
}

export const SUMMARY_CARD_CONFIGS: Partial<Record<MetricCardType, SummaryCardConfig>> = {
  otdc: {
    target: 95,
    valueField: "hit_rate",
    prevField: "prev_hit_rate",
    cumLabel: "Cum Shipment",
    cumField: "quantity_of_shipped",
    details: [
      { label: "Actual", field: "actual_shipment_qty" },
      { label: "Requested", field: "requested_shipment_qty" },
      { label: "Gap", field: "gap_shipment", showGapColor: true },
    ],
  },
  line_utilization: {
    target: 90,
    valueField: "line_utilization",
    prevField: "prev_line_utilization",
    cumLabel: "Cum Output",
    cumField: "cum_output",
    details: [
      { label: "Wk Output", field: "weekly_output" },
      { label: "Wk Instal", field: "weekly_cap" },
      { label: "Cum", field: "cum_utilization", percent: true },
    ],
  },
};
